import {
  commitFieldValue,
  FieldWrapper,
  getFieldErrorMessage,
} from "@birdwatch/ui/form";
import { cn } from "cn";
import { type ReactNode, useRef } from "react";
import ListItemAttachment from "@/components/ui/attachment/list-item-attachment";
import { BwCloudUploadLine } from "@/components/ui/icons";
import {
  ALLOW_DOCUMENTS,
  ALLOW_IMAGES,
} from "@/features/shared/upload/_libs/upload-file-type-default";
import { Upload } from "@/features/shared/upload/invisible-upload";
import {
  type FieldBindingProps,
  PolyField,
  pickBinding,
} from "@/fields/_internal/poly-field";
import type { AttachmentUploadHandle } from "@/libs/attachment-upload-handle";
import type { FormAttachment } from "@/libs/form-attachment";

export const DOCUMENT_UPLOAD_FILE_TYPES = [...ALLOW_DOCUMENTS, ...ALLOW_IMAGES];

type DocumentsFieldBaseProps = {
  label?: ReactNode;
  description?: ReactNode;
  optional?: boolean;
  disabled?: boolean;
  className?: string;
  /** Copy on the dashed upload trigger. Default "Upload files...". */
  triggerLabel?: string;
  /** Default {@link DOCUMENT_UPLOAD_FILE_TYPES}. */
  allowFileTypes?: string[];
  /** Mirrors the uploader's own prop. Default `true`. */
  isPrivate?: boolean;
};

export type DocumentsFieldProps = DocumentsFieldBaseProps & FieldBindingProps;

export function DocumentsField(props: DocumentsFieldProps) {
  const {
    label,
    description,
    optional,
    disabled,
    className,
    triggerLabel = "Upload files...",
    allowFileTypes = DOCUMENT_UPLOAD_FILE_TYPES,
    isPrivate = true,
  } = props;
  const binding = pickBinding(props);
  const uploadRef = useRef<AttachmentUploadHandle | null>(null);

  return (
    <PolyField<FormAttachment[]> {...binding}>
      {(field) => {
        const value = field.state.value ?? [];
        return (
          <FieldWrapper
            label={label}
            description={description}
            optional={optional}
            error={getFieldErrorMessage(field)}
            className={className}
          >
            <div className="space-y-2">
              {value.map((file) => (
                <ListItemAttachment
                  key={file.uid || file.originalContentUri || file.id}
                  attachment={file}
                  status={file.status}
                  percent={file.percent}
                  onDelete={(e) => {
                    e.preventDefault();
                    // In-flight and errored rows have no URI yet — uid is the
                    // only key that exists for every row.
                    if (file.uid) {
                      uploadRef.current?.removeAttachmentByUid(file.uid);
                    } else {
                      uploadRef.current?.removeAttachmentByUri(
                        file.originalContentUri ?? "",
                      );
                    }
                  }}
                />
              ))}

              <Upload
                ref={uploadRef}
                value={value}
                onChange={(next) => commitFieldValue(field, next)}
                isPrivate={isPrivate}
                allowFileTypes={allowFileTypes}
                disabled={disabled}
              >
                <label
                  className={cn(
                    /* sorted */
                    "flex",
                    "items-center gap-2",
                    "h-11",
                    "px-4 py-3",
                    "bg-bg-dark-min border border-dashed border-border-dark-low rounded-md cursor-pointer",
                  )}
                >
                  <BwCloudUploadLine
                    size={24}
                    className="text-text-dark-high"
                  />
                  <span className="text-sm leading-5 font-normal text-text-dark-high">
                    {triggerLabel}
                  </span>
                </label>
              </Upload>
            </div>
          </FieldWrapper>
        );
      }}
    </PolyField>
  );
}
